import { create } from 'zustand';

// Tipos públicos del store de tema.
export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeState {
  // Preferencia del usuario.
  theme: Theme;
  // Tema efectivo después de resolver `system` contra `prefers-color-scheme`.
  resolved: ResolvedTheme;
  // Setter persistente.
  setTheme: (t: Theme) => void;
}

// Clave de localStorage usada también por el FOUC guard inline en `index.html`.
const STORAGE_KEY = 'cef:theme';

// Lee el tema desde localStorage. Si está bloqueado o no es válido, devuelve `'system'`.
function readStoredTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // localStorage bloqueado (modo privado / SSR / sandbox).
  }
  return 'system';
}

// Persiste el tema. Silencia errores de localStorage bloqueado.
function writeStoredTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // localStorage bloqueado; el store sigue funcionando en memoria.
  }
}

// Detecta si el sistema prefiere tema oscuro.
function prefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

// Resuelve el tema efectivo a partir de la preferencia.
function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') return prefersDark() ? 'dark' : 'light';
  return theme;
}

const initialTheme: Theme =
  typeof window === 'undefined' ? 'system' : readStoredTheme();
const initialResolved: ResolvedTheme = resolveTheme(initialTheme);

export const useThemeStore = create<ThemeState>((set) => ({
  theme: initialTheme,
  resolved: initialResolved,
  setTheme: (t) => {
    writeStoredTheme(t);
    set({ theme: t, resolved: resolveTheme(t) });
  },
}));

// Suscripción reactiva a `prefers-color-scheme` cuando el modo es `system`.
// Se ejecuta una sola vez al cargar el módulo en el browser.
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (event: MediaQueryListEvent): void => {
    if (useThemeStore.getState().theme !== 'system') return;
    useThemeStore.setState({ resolved: event.matches ? 'dark' : 'light' });
  };
  // Compat: navegadores modernos exponen addEventListener; los antiguos `addListener`.
  if (typeof media.addEventListener === 'function') {
    media.addEventListener('change', handler);
  } else {
    type LegacyMQL = MediaQueryList & {
      addListener: (cb: (e: MediaQueryListEvent) => void) => void;
    };
    (media as LegacyMQL).addListener(handler);
  }
}
